import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    LinearProgress,
    Chip,
    Grid,
    Paper,
    Divider,
    IconButton,
    Tooltip,
    Link,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import { PortfolioLpoa } from '../../clients/graphql/pages/lpoa-queries';
import { 
    getLpoaStatus, 
    isDocumentCreated, 
    isRejected, 
    isRevoked 
} from '../../utils/lpoaStatus';
import { formatMoment } from '../../functions/momentFormatter';

interface LpoaDetailsModalProps {
    open: boolean;
    onClose: () => void;
    lpoaData: PortfolioLpoa[];
    userKey: string | number;
}

export const LpoaDetailsModal: React.FC<LpoaDetailsModalProps> = ({
    open,
    onClose,
    lpoaData,
    userKey
}) => {
    const navigate = useNavigate();
    const getLpoaState = (lpoa: PortfolioLpoa): string => {
        if (isRejected(lpoa)) return 'rejected';
        if (isRevoked(lpoa)) return 'revoked';
        
        const status = getLpoaStatus(lpoa);
        if (status.isActive) return 'active';
        if (status.isSigned) return 'signed';
        if (isDocumentCreated(lpoa)) return 'document-created';
        return 'empty';
    };

    const getStateColor = (state: string): string => {
        switch (state) {
            case 'empty': return '#e0e0e0';
            case 'document-created': return '#ff9800';
            case 'signed': return '#2196f3';
            case 'active': return '#4caf50';
            case 'rejected': return '#f44336';
            case 'revoked': return '#9e9e9e';
            default: return '#e0e0e0';
        }
    };

    const getStateLabel = (state: string): string => {
        switch (state) {
            case 'empty': return 'Empty';
            case 'document-created': return 'Document Created';
            case 'signed': return 'Signed';
            case 'active': return 'Active';
            case 'rejected': return 'Rejected';
            case 'revoked': return 'Revoked';
            default: return 'Unknown';
        }
    };

    const getProgressPercentage = (state: string): number => {
        switch (state) {
            case 'empty': return 0;
            case 'document-created': return 20;
            case 'signed': return 50;
            case 'active': return 100;
            case 'rejected': return 0;
            case 'revoked': return 0;
            default: return 0;
        }
    };

    const getStatusMessage = (lpoa: PortfolioLpoa, state: string): string => {
        switch (state) {
            case 'empty':
                return `LPOA record created on ${formatMoment(lpoa.createdAt.toLocaleString())} - No DocuSign envelope ID assigned yet`;
            case 'document-created':
                return `Document created on ${formatMoment(lpoa.createdAt.toLocaleString())} - Awaiting signature`;
            case 'signed':
                return `Signed on ${formatMoment(lpoa.signedAt!.toLocaleString())} - Awaiting activation`;
            case 'active':
                return `Activated on ${formatMoment(lpoa.activatedAt!.toLocaleString())} - Portfolio is managed under LPOA`;
            case 'rejected':
                return `Rejected on ${formatMoment(lpoa.rejectedAt!.toLocaleString())} - Schwab rejected the signature`;
            case 'revoked':
                return `Revoked on ${formatMoment(lpoa.revokedAt!.toLocaleString())} - User removed LPOA access`;
            default:
                return '';
        }
    };

    // Sort LPOA data by portfolio key for consistent display
    const sortedLpoaData = [...lpoaData].sort((a, b) => a.portfolioKey - b.portfolioKey);

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="lg" 
            fullWidth
            PaperProps={{
                sx: { minHeight: '60vh' }
            }}
        >
            <DialogTitle>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    LPOA Details - User #{userKey}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Per-Portfolio LPOA Status Overview
                </Typography>
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    {sortedLpoaData.map((lpoa, index) => {
                        const state = getLpoaState(lpoa);
                        const stateColor = getStateColor(state);
                        const stateLabel = getStateLabel(state);
                        const progressPercentage = getProgressPercentage(state);
                        const statusMessage = getStatusMessage(lpoa, state);

                        return (
                            <Paper 
                                key={lpoa.portfolioLpoaKey} 
                                elevation={1}
                                sx={{ 
                                    p: 2, 
                                    mb: 2,
                                    border: `1px solid ${stateColor}20`,
                                    borderRadius: 2
                                }}
                            >
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                        <Box>
                                            <Link
                                                component="button"
                                                variant="subtitle1"
                                                sx={{ 
                                                    fontWeight: 'bold', 
                                                    color: 'primary.main',
                                                    textDecoration: 'none',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        textDecoration: 'underline',
                                                        color: 'primary.dark'
                                                    }
                                                }}
                                                onClick={() => navigate(`/portfolios/${lpoa.portfolioKey}`)}
                                            >
                                                Portfolio #{lpoa.portfolioKey}
                                            </Link>
                                            <Typography variant="body2" color="text.secondary">
                                                {lpoa.portfolio.brokerName} - {lpoa.portfolio.accountId}
                                            </Typography>
                                            {lpoa.portfolio.nickname && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {lpoa.portfolio.nickname}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={2}>
                                        <Chip
                                            label={stateLabel}
                                            sx={{
                                                backgroundColor: stateColor,
                                                color: 'white',
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                width: '100%',
                                                justifyContent: 'center'
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={7}>
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                    Progress
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                    {progressPercentage}%
                                                </Typography>
                                            </Box>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={progressPercentage}
                                                sx={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: stateColor,
                                                        borderRadius: 3,
                                                    }
                                                }}
                                            />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                    Created
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                    Signed
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                    Active
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                                
                                <Divider sx={{ my: 1.5 }} />
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {statusMessage}
                                    </Typography>
                                    {state === 'rejected' && lpoa.rejectedReason && (
                                        <Tooltip 
                                            title={
                                                <Box sx={{ width: 500, p: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: 'white' }}>
                                                        Schwab Rejection Details
                                                    </Typography>
                                                    <Box sx={{ 
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                                                        borderRadius: 1, 
                                                        p: 2,
                                                        border: '1px solid rgba(255, 255, 255, 0.2)'
                                                    }}>
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                whiteSpace: 'pre-wrap',
                                                                fontSize: '0.9rem',
                                                                lineHeight: 1.7,
                                                                color: 'white',
                                                                wordBreak: 'break-word'
                                                            }}
                                                        >
                                                            {lpoa.rejectedReason}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            }
                                            arrow
                                            placement="right"
                                            PopperProps={{
                                                modifiers: [
                                                    {
                                                        name: 'preventOverflow',
                                                        enabled: true,
                                                        options: {
                                                            boundary: 'viewport',
                                                            padding: 16,
                                                        },
                                                    },
                                                    {
                                                        name: 'flip',
                                                        enabled: true,
                                                        options: {
                                                            fallbackPlacements: ['left', 'bottom', 'top'],
                                                        },
                                                    },
                                                    {
                                                        name: 'offset',
                                                        enabled: true,
                                                        options: {
                                                            offset: [8, 0],
                                                        },
                                                    },
                                                ],
                                            }}
                                            slotProps={{
                                                popper: {
                                                    sx: {
                                                        zIndex: 9999,
                                                        '& .MuiTooltip-tooltip': {
                                                            maxWidth: 'none',
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                                                <InfoIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                                
                                {lpoa.envelopeId && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Envelope ID: {lpoa.envelopeId}
                                    </Typography>
                                )}
                            </Paper>
                        );
                    })}
                </Box>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};